import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RemoveIcon from '@mui/icons-material/Remove';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { createContext, useContext, type ReactNode } from 'react';

import { formatPercent } from '@/lib/numbers';

/**
 * How the number compares with the period before. `percent` is null when the
 * previous number was zero, so there is no percentage to give.
 */
export interface StatChange {
  percent: number | null;
  direction: 'up' | 'down' | 'flat';
}

interface StatCardProps {
  label: string;
  value: number | string;
  /** The small line underneath, explaining what the number counts. */
  caption?: string;
  /** The change against the previous period, shown between value and caption. */
  change?: StatChange;
  /**
   * True while the number is on its way: the value, and the change line when
   * there will be one, are shown as placeholders the size of what replaces
   * them, so nothing moves when it arrives.
   */
  loading?: boolean;
}

/** How many cards the row fits across at desktop width. */
const ColumnsContext = createContext<3 | 4>(4);

/** The desktop share of one card: the row's width less its gaps, divided. */
const DESKTOP_SHARE: Record<3 | 4, string> = {
  // Two gaps of 16px shared between three cards.
  3: 'calc((100% - 32px) / 3)',
  4: 'calc(25% - 12px)',
};

/** Read by a screen reader, not shown. */
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

const DIRECTION = {
  up: { Icon: ArrowUpwardIcon, word: 'up', colour: 'success.main' },
  down: { Icon: ArrowDownwardIcon, word: 'down', colour: 'error.main' },
  flat: { Icon: RemoveIcon, word: 'no change', colour: 'text.secondary' },
} as const;

/**
 * The change line. The icon and the words carry the meaning; the colour only
 * repeats it, so it reads the same to somebody who cannot tell red from green.
 *
 * What is seen ("6.4% vs. previous period" and an arrow) is hidden from a
 * screen reader, and a sentence is read in its place - "up 6.4 percent versus
 * the previous period" - rather than an unnamed picture and a bare number.
 */
function ChangeLine({ change }: { change: StatChange }) {
  if (change.percent === null) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        No previous data
      </Typography>
    );
  }

  const { Icon, word, colour } = DIRECTION[change.direction];
  // The arrow already says which way, so the number is shown without a sign.
  const size = Math.abs(change.percent);
  const spoken =
    change.direction === 'flat'
      ? 'no change versus the previous period'
      : `${word} ${size.toFixed(1)} percent versus the previous period`;

  return (
    <Typography
      variant="body2"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: colour }}
    >
      <Icon fontSize="small" aria-hidden="true" />
      <Box component="span" aria-hidden="true">
        {formatPercent(size)}{' '}
        <Box component="span" sx={{ color: 'text.secondary' }}>
          vs. previous period
        </Box>
      </Box>
      <Box component="span" sx={visuallyHidden}>
        {spoken}
      </Box>
    </Typography>
  );
}

/** One summary number, as the wireframes show above every list. */
export function StatCard({
  label,
  value,
  caption,
  change,
  loading = false,
}: StatCardProps) {
  const columns = useContext(ColumnsContext);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 0,
        /*
         * Never grow. With `flex: 1 1 180px` four cards broke to three and one
         * at medium widths, and the orphan stretched across the whole row -
         * which reads as a layout fault rather than a count. A fixed share
         * gives two rows of two instead, and one card a row on a phone, where
         * two side by side leave each number a few characters wide.
         */
        flex: {
          xs: '1 1 100%',
          sm: '1 1 calc(50% - 8px)',
          md: `1 1 ${DESKTOP_SHARE[columns]}`,
        },
      }}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        component="h2"
        sx={{ display: 'block', lineHeight: 1.6 }}
      >
        {label}
      </Typography>
      <Typography variant="h4" component="p" sx={{ fontWeight: 600, my: 0.5 }}>
        {loading ? <Skeleton width="60%" /> : value}
      </Typography>
      {loading && change !== undefined ? (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <Skeleton width="80%" />
        </Typography>
      ) : change ? (
        <ChangeLine change={change} />
      ) : null}
      {caption ? (
        <Typography variant="body2" color="text.secondary">
          {caption}
        </Typography>
      ) : null}
    </Paper>
  );
}

interface StatCardRowProps {
  /** Names the group for a screen reader. */
  label?: string;
  /**
   * How many cards fit across at desktop width. Three suits six cards, which
   * then make two full rows instead of a row of four and two orphans.
   */
  columns?: 3 | 4;
  children: ReactNode;
}

/**
 * The row the summary cards sit in: four (or three) across on a desktop, two
 * on a tablet, one on a phone.
 *
 * A named group, so a screen reader announces the numbers as one set, and so
 * the browser tests find every row of summary cards on every screen by its
 * role, without a list of which screens have one.
 */
export function StatCardRow({
  label = 'Summary',
  columns = 4,
  children,
}: StatCardRowProps) {
  return (
    <ColumnsContext.Provider value={columns}>
      <Box
        role="group"
        aria-label={label}
        // Read by the browser tests, which check each row is as many across
        // as it says.
        data-columns={columns}
        sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}
      >
        {children}
      </Box>
    </ColumnsContext.Provider>
  );
}
